import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Popover,
  Dropdown,
  Tabs,
  Typography,
  Drawer,
  Spin,
  Modal,
  Space,
  Tag,
} from "antd";
import {
  BellOutlined,
  MoreOutlined,
  CheckOutlined,
  DeleteOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  AlertOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications,
} from "../../redux/action/notifications";
import { fetchBroadcast } from "../../redux/action/broadcasts";
import { getFullUrl } from "constants/func";
import { colors } from "../../theme/theme";
import { useResponsive } from "../../hooks/useResponsive";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";

dayjs.extend(relativeTime);
dayjs.locale("vi");

const { Text, Title } = Typography;

const PANEL_W = 400;
const NOTIFICATION_PAGE_SIZE = 10;

const getNotificationPageData = (response) => {
  const items = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];
  const total = Number(response?.total);

  return {
    items,
    total: Number.isFinite(total) ? total : undefined,
  };
};

// Dynamic style sheet for keyframes and complex classes
const STYLE_INJECTION = `
  @keyframes notif-pulse-red {
    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
  }
  @keyframes notif-pulse-amber {
    0% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(217, 119, 6, 0); }
    100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
  }
  @keyframes notif-pulse-blue {
    0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
    100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  }
  @keyframes bell-swing {
    0% { transform: rotate(0); }
    8% { transform: rotate(15deg); }
    16% { transform: rotate(-12deg); }
    24% { transform: rotate(10deg); }
    32% { transform: rotate(-8deg); }
    40% { transform: rotate(6deg); }
    48% { transform: rotate(-4deg); }
    56% { transform: rotate(2deg); }
    64% { transform: rotate(0); }
    100% { transform: rotate(0); }
  }
  @keyframes slide-in {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .notif-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .notif-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    background: #FFFFFF !important;
  }
  
  .action-btn {
    transition: all 0.2s ease;
  }
  .action-btn:hover {
    transform: scale(1.15);
    background: rgba(255, 255, 255, 1) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .swinging-bell {
    animation: bell-swing 3s infinite ease-in-out;
    transform-origin: top center;
  }
  
  /* Custom Tabs Overrides */
  .custom-notif-tabs .ant-tabs-nav {
    margin-bottom: 0 !important;
  }
  .custom-notif-tabs .ant-tabs-nav-list {
    gap: 6px !important; /* Reduce gap between tabs */
  }
  .custom-notif-tabs .ant-tabs-tab {
    padding: 10px 4px !important; /* Reduce padding for tabs */
    font-weight: 500 !important;
    transition: all 0.25s !important;
  }
  .custom-notif-tabs .ant-tabs-tab-active {
    font-weight: 700 !important;
  }
`;

const PRIORITY_STYLE = {
  critical: {
    dot: "#DC2626",
    border: "linear-gradient(to bottom, #DC2626, #EF4444)",
    bg: "linear-gradient(135deg, rgba(254, 242, 242, 0.8) 0%, rgba(254, 242, 242, 0.4) 100%)",
    pulse: "notif-pulse-red 2s infinite",
    shadow: "rgba(220, 38, 38, 0.08)",
  },
  urgent: {
    dot: "#DC2626",
    border: "linear-gradient(to bottom, #DC2626, #EF4444)",
    bg: "linear-gradient(135deg, rgba(254, 242, 242, 0.8) 0%, rgba(254, 242, 242, 0.4) 100%)",
    pulse: "notif-pulse-red 2s infinite",
    shadow: "rgba(220, 38, 38, 0.08)",
  },
  high: {
    dot: "#DC2626",
    border: "linear-gradient(to bottom, #DC2626, #EF4444)",
    bg: "linear-gradient(135deg, rgba(254, 242, 242, 0.8) 0%, rgba(254, 242, 242, 0.4) 100%)",
    pulse: "notif-pulse-red 2s infinite",
    shadow: "rgba(220, 38, 38, 0.08)",
  },
  warning: {
    dot: "#D97706",
    border: "linear-gradient(to bottom, #D97706, #F59E0B)",
    bg: "linear-gradient(135deg, rgba(255, 251, 235, 0.8) 0%, rgba(255, 251, 235, 0.4) 100%)",
    pulse: "notif-pulse-amber 2s infinite",
    shadow: "rgba(217, 119, 6, 0.08)",
  },
  medium: {
    dot: "#D97706",
    border: "linear-gradient(to bottom, #D97706, #F59E0B)",
    bg: "linear-gradient(135deg, rgba(255, 251, 235, 0.8) 0%, rgba(255, 251, 235, 0.4) 100%)",
    pulse: "notif-pulse-amber 2s infinite",
    shadow: "rgba(217, 119, 6, 0.08)",
  },
  info: {
    dot: "#2563EB",
    border: `linear-gradient(to bottom, #2563EB, #3B82F6)`,
    bg: "linear-gradient(135deg, rgba(240, 249, 255, 0.6) 0%, rgba(240, 249, 255, 0.3) 100%)",
    pulse: "notif-pulse-blue 2s infinite",
    shadow: "rgba(37, 99, 235, 0.05)",
  },
  low: {
    dot: "#2563EB",
    border: `linear-gradient(to bottom, #2563EB, #3B82F6)`,
    bg: "linear-gradient(135deg, rgba(240, 249, 255, 0.6) 0%, rgba(240, 249, 255, 0.3) 100%)",
    pulse: "notif-pulse-blue 2s infinite",
    shadow: "rgba(37, 99, 235, 0.05)",
  },
};

const CATEGORY_TAB_FALLBACK = {
  feedback: "feedback",
  schedule: "schedule",
  event: "schedule",
  system: "directive",
  resident: "directive",
  directive: "directive",
};

const TABS = [
  { key: "all", label: "Tất cả" },
  { key: "feedback", label: "Phản ánh" },
  { key: "schedule", label: "Lịch tiếp dân" },
  { key: "directive", label: "Bản tin" },
];

const SCHEDULE_NOTIFICATION_TYPES = new Set([
  "SCHEDULE_URGENT",
  "APPOINTMENT_NEW",
  "APPOINTMENT_CANCELLED",
  "APPOINTMENT_REMINDER",
]);

const BROADCAST_NOTIFICATION_TYPES = new Set(["BROADCAST_NEW"]);

const createPaginationState = () =>
  TABS.reduce((state, tab) => {
    state[tab.key] = {
      nextPage: 1,
      loadedPages: 0,
      hasMore: true,
    };
    return state;
  }, {});

const getNotificationTabKey = (notification = {}) => {
  if (notification.type === "BROADCAST_NEW") return "directive";
  if (notification.type === "DIRECTIVE_NEW") return "feedback";
  if (SCHEDULE_NOTIFICATION_TYPES.has(notification.type)) return "schedule";
  return CATEGORY_TAB_FALLBACK[notification.category];
};

function formatRelative(dateStr) {
  if (!dateStr) return "";
  return dayjs(dateStr).fromNow();
}

function notifIcon(n) {
  const sz = 18;
  const p = n.priority?.toLowerCase();

  if (n.type === "BROADCAST_NEW") {
    return (
      <BellOutlined style={{ fontSize: sz, color: colors.primary[600] }} />
    );
  }
  if (n.type === "DIRECTIVE_NEW") {
    return <AlertOutlined style={{ fontSize: sz, color: "#DC2626" }} />;
  }
  if (n.type === "SCHEDULE_URGENT") {
    return <CalendarOutlined style={{ fontSize: sz, color: "#D97706" }} />;
  }
  if (n.type === "INCIDENT_OVERDUE_RECEIVE" || n.type === "INCIDENT_OVERDUE") {
    return (
      <ExclamationCircleOutlined style={{ fontSize: sz, color: "#D97706" }} />
    );
  }
  if (n.type === "INCIDENT_ESCALATED") {
    return (
      <ExclamationCircleOutlined style={{ fontSize: sz, color: "#DC2626" }} />
    );
  }
  if (n.category === "schedule" || n.category === "event") {
    return (
      <CalendarOutlined style={{ fontSize: sz, color: colors.primary[600] }} />
    );
  }
  if (n.category === "system") {
    return <InfoCircleOutlined style={{ fontSize: sz, color: "#2563EB" }} />;
  }
  if (p === "critical" || p === "high" || p === "urgent") {
    return (
      <ExclamationCircleOutlined style={{ fontSize: sz, color: "#DC2626" }} />
    );
  }
  if (p === "warning" || p === "medium") {
    return <AlertOutlined style={{ fontSize: sz, color: "#D97706" }} />;
  }
  return <InfoCircleOutlined style={{ fontSize: sz, color: "#2563EB" }} />;
}

function navigateTarget(n) {
  if (n?.link) {
    return n.link;
  } else {
    if (n.type === "BROADCAST_NEW") return "/broadcasts";
    if (n.category === "schedule" || n.category === "event")
      return "/appointments";
    if (n.category === "system" || n.category === "resident") return "/";
    return "/feedback";
  }
}

export function filterNotificationsByRole(notifications, role) {
  if (!role) return notifications;
  if (role === "chief" || role === "deputy_chief") return notifications;
  if (role === "team_leader") {
    return notifications.filter((n) => n.type !== "INCIDENT_ESCALATED");
  }
  return notifications.filter(
    (n) => n.type !== "INCIDENT_ESCALATED" && n.type !== "SCHEDULE_URGENT",
  );
}

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

function NotifItem({ index, n, onRead, onDelete, onNavigate }) {
  const s = PRIORITY_STYLE[n.priority?.toLowerCase()] || PRIORITY_STYLE.info;
  const [hovered, setHovered] = useState(false);
  const { isMobile } = useResponsive();
  const animationDelay = Math.min(index, 6) * 0.025;

  return (
    <div
      role="button"
      className="notif-card"
      tabIndex={0}
      onClick={() => {
        onNavigate();
        if (!n.isRead) {
          onRead();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onNavigate();
          if (!n.isRead) {
            onRead();
          }
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        gap: 12,
        margin: "8px 12px",
        padding: "12px 14px",
        borderRadius: 12,
        border: "1px solid #F1F5F9",
        background: n.isRead ? "#FFFFFF" : s.bg,
        boxShadow: hovered
          ? `0 6px 16px ${s.shadow}`
          : "0 2px 4px rgba(15, 23, 42, 0.01)",
        cursor: "pointer",
        position: "relative",
        animation: `slide-in 0.25s cubic-bezier(0.16, 1, 0.3, 1) ${animationDelay}s both`,
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          background: n.isRead ? "#F1F5F9" : "#FFFFFF",
          border: `1.5px solid ${n.isRead ? "#E2E8F0" : s.dot}33`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: n.isRead ? "none" : "0 2px 6px rgba(0, 0, 0, 0.03)",
        }}
      >
        {notifIcon(n)}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: n.isRead ? 600 : 800,
            color: n.isRead ? "#334155" : "#0F172A",
            lineHeight: 1.35,
            marginBottom: 4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {n.title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: n.isRead ? "#64748B" : "#475569",
            lineHeight: 1.45,
            fontWeight: n.isRead ? 400 : 500,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            whiteSpace: "pre-line",
            overflow: "hidden",
          }}
        >
          {n.type === "BROADCAST_NEW"
            ? n.data?.broadcast?.rawContent || n.rawContent || n.content
            : n.content}
        </div>
        <div
          style={{
            fontSize: 10,
            color: "#94A3B8",
            marginTop: 6,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "#CBD5E1",
            }}
          />
          {formatRelative(n.createdAt)}
        </div>
      </div>

      {/* Unread pulsing dot & Mobile quick actions menu */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginLeft: 6,
          flexShrink: 0,
          alignSelf: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!n.isRead && (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: s.dot,
              flexShrink: 0,
              animation: s.pulse,
            }}
          />
        )}
        {isMobile && (
          <Dropdown
            menu={{
              items: [
                ...(!n.isRead
                  ? [
                      {
                        key: "read",
                        icon: (
                          <CheckOutlined
                            style={{ fontSize: 13, color: colors.primary[600] }}
                          />
                        ),
                        label: "Đánh dấu đã đọc",
                        onClick: (info) => {
                          info.domEvent.stopPropagation();
                          onRead();
                        },
                      },
                    ]
                  : []),
                {
                  key: "delete",
                  icon: <DeleteOutlined style={{ fontSize: 13 }} />,
                  label: "Xóa thông báo",
                  danger: true,
                  onClick: (info) => {
                    info.domEvent.stopPropagation();
                    onDelete();
                  },
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined style={{ fontSize: 16, color: "#64748B" }} />}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#F1F5F9",
                padding: 0,
              }}
            />
          </Dropdown>
        )}
      </div>

      {/* Quick actions (shown on hover) with scale fade animation on Desktop */}
      {!isMobile && hovered && (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            gap: 6,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(4px)",
            borderRadius: 8,
            padding: "4px",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
            border: "1px solid #E2E8F0",
            animation: "slide-in 0.15s ease-out",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {!n.isRead && (
            <button
              type="button"
              className="action-btn"
              title="Đã đọc"
              onClick={onRead}
              style={{
                all: "unset",
                width: 24,
                height: 24,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: colors.primary[600],
                background: colors.primary[50],
                fontSize: 11,
              }}
            >
              <CheckOutlined />
            </button>
          )}
          <button
            type="button"
            className="action-btn"
            title="Xóa"
            onClick={onDelete}
            style={{
              all: "unset",
              width: 24,
              height: 24,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#EF4444",
              background: "#FEF2F2",
              fontSize: 11,
            }}
          >
            <DeleteOutlined />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state (with swaying bell animation)
// ---------------------------------------------------------------------------

function EmptyNotif() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "36px 24px",
        gap: 14,
        background: "linear-gradient(to bottom, #FFFFFF, #F8FAFC)",
      }}
    >
      <div
        className="swinging-bell"
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            "inset 0 2px 4px rgba(255,255,255,0.8), 0 4px 12px rgba(15,23,42,0.03)",
        }}
      >
        <BellOutlined style={{ fontSize: 28, color: "#94A3B8" }} />
      </div>
      <div style={{ textAlign: "center" }}>
        <Text
          strong
          style={{
            fontSize: 14,
            color: "#64748B",
            display: "block",
            letterSpacing: "0.01em",
            marginBottom: 2,
          }}
        >
          Không có thông báo mới
        </Text>
        <Text style={{ fontSize: 11, color: "#94A3B8" }}>
          Chúng tôi sẽ cập nhật khi có thông tin mới
        </Text>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel content (shown inside Popover)
// ---------------------------------------------------------------------------

function PanelContent({
  role,
  onNavigate,
  onClose,
  isMobile,
  isOpen,
  onShowBroadcastModal,
}) {
  const [activeTab, setActiveTab] = useState("all");
  const dispatch = useDispatch();

  const all = useSelector((state) => state.notifications.notifications);
  const loading = useSelector((state) => state.notifications.loading);
  const unreadCountsByTab = useSelector(
    (state) => state.notifications.unreadCountsByTab,
  );
  const items = filterNotificationsByRole(all, role);
  const localUnreadCountsByTab = TABS.reduce(
    (counts, tab) => ({
      ...counts,
      [tab.key]:
        tab.key === "all"
          ? items.filter((n) => !n.isRead).length
          : items.filter(
              (n) => !n.isRead && getNotificationTabKey(n) === tab.key,
            ).length,
    }),
    {},
  );
  const getUnreadCount = (tabKey) => {
    const apiCount = Number(unreadCountsByTab?.[tabKey]);
    return Number.isFinite(apiCount)
      ? apiCount
      : localUnreadCountsByTab[tabKey] || 0;
  };
  const unread = getUnreadCount("all");

  const filtered =
    activeTab === "all"
      ? items
      : items.filter((n) => getNotificationTabKey(n) === activeTab);

  const [paginationByTab, setPaginationByTab] = useState(createPaginationState);
  const [loadingByTab, setLoadingByTab] = useState({});
  const paginationRef = useRef(createPaginationState());
  const loadingTabsRef = useRef(new Set());

  const updateTabPagination = useCallback((tabKey, nextValue) => {
    const currentTab = paginationRef.current[tabKey] || {
      nextPage: 1,
      loadedPages: 0,
      hasMore: true,
    };
    const nextTab =
      typeof nextValue === "function"
        ? nextValue(currentTab)
        : { ...currentTab, ...nextValue };
    const nextState = {
      ...paginationRef.current,
      [tabKey]: nextTab,
    };

    paginationRef.current = nextState;
    setPaginationByTab(nextState);
  }, []);

  const resetPagination = useCallback(() => {
    const nextState = createPaginationState();
    paginationRef.current = nextState;
    setPaginationByTab(nextState);
  }, []);

  const stopPagination = useCallback(() => {
    const nextState = createPaginationState();
    Object.keys(nextState).forEach((key) => {
      nextState[key] = {
        ...nextState[key],
        loadedPages: 1,
        hasMore: false,
      };
    });
    paginationRef.current = nextState;
    setPaginationByTab(nextState);
  }, []);

  const setTabLoading = useCallback((tabKey, value) => {
    setLoadingByTab((prev) => {
      if (!!prev[tabKey] === value) return prev;
      return { ...prev, [tabKey]: value };
    });
  }, []);

  const loadPage = useCallback(
    async (tabKey, pageToLoad, append) => {
      const currentPageState = paginationRef.current[tabKey] || {
        nextPage: 1,
        loadedPages: 0,
        hasMore: true,
      };

      if (
        loadingTabsRef.current.has(tabKey) ||
        (append && !currentPageState.hasMore)
      ) {
        return;
      }

      loadingTabsRef.current.add(tabKey);
      setTabLoading(tabKey, true);

      try {
        const response = await dispatch(
          fetchNotifications(
            pageToLoad,
            NOTIFICATION_PAGE_SIZE,
            append,
            tabKey === "all" ? {} : { tab: tabKey },
          ),
        );
        const { items: pageItems } = getNotificationPageData(response);

        updateTabPagination(tabKey, (prev) => ({
          ...prev,
          nextPage: pageToLoad + 1,
          loadedPages: Math.max(prev.loadedPages || 0, pageToLoad),
          hasMore: pageItems.length >= NOTIFICATION_PAGE_SIZE,
        }));
      } catch (err) {
        console.error("Failed to load notifications:", err);
      } finally {
        loadingTabsRef.current.delete(tabKey);
        setTabLoading(tabKey, false);
      }
    },
    [dispatch, setTabLoading, updateTabPagination],
  );

  const loadMore = useCallback(
    (tabKey = activeTab) => {
      const currentPageState = paginationRef.current[tabKey];
      if (!currentPageState?.hasMore) return;
      loadPage(tabKey, currentPageState.nextPage, true);
    },
    [activeTab, loadPage],
  );

  const handleReload = async () => {
    if (loadingTabsRef.current.size > 0) return;

    resetPagination();
    await loadPage("all", 1, false);
  };

  useEffect(() => {
    if (!isOpen) return;

    resetPagination();
    loadPage("all", 1, false);
  }, [isOpen, loadPage, resetPagination]);

  const activePagination = paginationByTab[activeTab] || {
    nextPage: 1,
    loadedPages: 0,
    hasMore: true,
  };
  const allLoadedPages = paginationByTab.all?.loadedPages || 0;

  useEffect(() => {
    if (!isOpen || activeTab === "all") return;

    if (allLoadedPages === 0) return;

    const currentPageState = paginationRef.current[activeTab];
    if (!currentPageState?.hasMore || loadingTabsRef.current.has(activeTab)) {
      return;
    }

    if (
      (currentPageState.loadedPages || 0) === 0 &&
      filtered.length >= NOTIFICATION_PAGE_SIZE
    ) {
      updateTabPagination(activeTab, {
        nextPage: 2,
        loadedPages: 1,
        hasMore: true,
      });
      return;
    }

    if (filtered.length < NOTIFICATION_PAGE_SIZE) {
      loadPage(activeTab, currentPageState.nextPage, true);
    }
  }, [
    activeTab,
    activePagination.hasMore,
    activePagination.loadedPages,
    activePagination.nextPage,
    allLoadedPages,
    filtered.length,
    isOpen,
    loadPage,
    updateTabPagination,
  ]);

  const activeLoading = !!loadingByTab[activeTab];
  const activeInitialLoading = activeLoading && filtered.length === 0;
  const activeLoadingMore = activeLoading && filtered.length > 0;
  const isFetching = Object.values(loadingByTab).some(Boolean);
  const shouldShowEmptyLoading =
    filtered.length === 0 &&
    (activeInitialLoading ||
      (activeTab !== "all" &&
        activePagination.hasMore &&
        activePagination.loadedPages === 0 &&
        allLoadedPages > 0));

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMore(activeTab);
    }
  };

  const tabsWithCount = TABS.map((t) => {
    const count = getUnreadCount(t.key);
    return {
      key: t.key,
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {t.label}
          {count > 0 && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: "0 6px",
                height: 14,
                lineHeight: "14px",
                borderRadius: 10,
                background: t.key === "all" ? "#DC2626" : colors.primary[500],
                color: "#FFFFFF",
              }}
            >
              {count}
            </span>
          )}
        </span>
      ),
    };
  });

  function handleDeleteAll() {
    dispatch(deleteAllNotifications());
    stopPagination();
  }

  const moreMenu = {
    items: [
      {
        key: "delete-all",
        icon: <DeleteOutlined style={{ fontSize: 13 }} />,
        label: "Xóa tất cả thông báo",
        danger: true,
        onClick: handleDeleteAll,
      },
    ],
  };

  return (
    <div
      style={{
        width: isMobile ? "100%" : PANEL_W,
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        height: isMobile ? "100%" : "auto",
        overflow: "hidden",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: STYLE_INJECTION }} />

      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "12px 14px 10px" : "16px 18px 12px",
          borderBottom: "1px solid #F1F5F9",
          gap: 8,
          flexShrink: 0,
          background: "linear-gradient(to bottom, #FCFDFD, #FFFFFF)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text
            strong
            style={{ fontSize: 16, color: "#0F172A", letterSpacing: "-0.01em" }}
          >
            Thông báo
          </Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined style={{ fontSize: 12 }} />}
            loading={loading || isFetching}
            onClick={handleReload}
            title="Tải lại"
            style={{
              fontSize: 12,
              color: colors.primary[600],
              padding: "0 8px",
              fontWeight: 600,
              background: colors.primary[50],
              borderRadius: 6,
              height: 26,
            }}
          ></Button>

          {unread > 0 && (
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined style={{ fontSize: 12 }} />}
              onClick={() => dispatch(markAllNotificationsRead())}
              style={{
                fontSize: 12,
                color: colors.primary[600],
                padding: "0 8px",
                fontWeight: 600,
                background: colors.primary[50],
                borderRadius: 6,
                height: 26,
              }}
            >
              Đọc tất cả
            </Button>
          )}
          <Dropdown menu={moreMenu} placement="bottomRight" trigger={["click"]}>
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined style={{ fontSize: 16 }} />}
              style={{
                color: "#64748B",
                background: "#F1F5F9",
                borderRadius: 6,
                width: 26,
                height: 26,
                padding: 0,
              }}
            />
          </Dropdown>
          {isMobile && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined style={{ fontSize: 14 }} />}
              onClick={onClose}
              style={{
                color: "#64748B",
                background: "#F1F5F9",
                borderRadius: 6,
                width: 26,
                height: 26,
                padding: 0,
                marginLeft: 4,
              }}
            />
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div
        className="custom-notif-tabs"
        style={{
          flexShrink: 0,
          borderBottom: "1px solid #F1F5F9",
          paddingLeft: isMobile ? 14 : 18,
          paddingRight: isMobile ? 14 : 18,
          background: "#FCFDFD",
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          items={tabsWithCount}
          style={{ marginBottom: 0 }}
          tabBarStyle={{ margin: 0, borderBottom: "none" }}
        />
      </div>

      {/* ── List ── */}
      <div
        onScroll={handleScroll}
        style={{
          overflowY: "auto",
          flex: isMobile ? "1 1 0" : "none",
          height: isMobile ? undefined : 380,
          flexShrink: 0,
          background: "#FCFDFD",
          padding: "4px 0",
        }}
      >
        {filtered.length === 0 ? (
          shouldShowEmptyLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "36px 24px",
                gap: 12,
              }}
            >
              <Text style={{ fontSize: 12, color: "#64748B" }}>
                Đang tải thêm...
              </Text>
              <Spin size="small" />
            </div>
          ) : (
            <EmptyNotif />
          )
        ) : (
          <>
            {filtered.map((n, i) => (
              <NotifItem
                key={n.id}
                index={i}
                n={n}
                onRead={() => dispatch(markNotificationRead(n.id))}
                onDelete={() => dispatch(deleteNotification(n.id))}
                onNavigate={() => {
                  if (n.type === "BROADCAST_NEW") {
                    onShowBroadcastModal(n);
                  } else {
                    onClose();
                    onNavigate(navigateTarget(n));
                  }
                }}
              />
            ))}
            {activeLoadingMore && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "12px 0",
                }}
              >
                <Spin size="small" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported bell trigger
// ---------------------------------------------------------------------------

export default function NotificationMiniPanel({
  role,
  isMobileTopbar,
  onCloseSidebar,
}) {
  const [open, setOpen] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastDetail, setBroadcastDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const match = location.pathname.match(/^\/broadcasts\/([^/]+)\/?$/i);
    if (match) {
      const broadcastId = match[1];
      if (
        !selectedBroadcast ||
        selectedBroadcast.data?.broadcastId !== broadcastId
      ) {
        setSelectedBroadcast({ data: { broadcastId } });
      }
    }
  }, [location.pathname, selectedBroadcast]);

  const handleCloseBroadcastModal = () => {
    setSelectedBroadcast(null);
    const match = location.pathname.match(/^\/broadcasts\/([^/]+)\/?$/i);
    if (match) {
      navigate("/");
    }
  };

  useEffect(() => {
    if (!selectedBroadcast) {
      setBroadcastDetail(null);
      return;
    }
    const broadcastId = selectedBroadcast.data?.broadcastId;
    if (broadcastId) {
      setLoadingDetail(true);
      dispatch(fetchBroadcast(broadcastId))
        .then((res) => {
          if (res) {
            setBroadcastDetail(res.data || res);
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoadingDetail(false);
        });
    } else {
      setBroadcastDetail({
        name: selectedBroadcast.data?.broadcastName || selectedBroadcast.title,
        title:
          selectedBroadcast.data?.broadcastTitle || selectedBroadcast.title,
        content: selectedBroadcast.content,
        createdAt: selectedBroadcast.createdAt,
        priority:
          selectedBroadcast.priority === "critical"
            ? "urgent"
            : selectedBroadcast.priority === "high"
              ? "important"
              : "normal",
        category: selectedBroadcast.category,
        officerCount: selectedBroadcast.data?.officerCount || 0,
        citizenCount: selectedBroadcast.data?.citizenCount || 0,
        author_id: selectedBroadcast.senderId
          ? {
              fullName:
                selectedBroadcast.senderId.fullName ||
                selectedBroadcast.senderId,
            }
          : { fullName: "Quản trị viên" },
      });
    }
  }, [selectedBroadcast, dispatch]);
  const { isMobile } = useResponsive();

  const all = useSelector((state) => state.notifications.notifications);
  const apiUnreadCount = useSelector(
    (state) => state.notifications.unreadCount,
  );
  const localUnread = filterNotificationsByRole(all, role).filter(
    (n) => !n.isRead,
  ).length;
  const parsedApiUnreadCount = Number(apiUnreadCount);
  const unread = Number.isFinite(parsedApiUnreadCount)
    ? parsedApiUnreadCount
    : localUnread;

  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;

    if (unread > 0) {
      navigator.setAppBadge(unread).catch(() => {});
      return;
    }

    navigator.clearAppBadge?.().catch(() => {});
  }, [unread]);

  if (isMobile) {
    return (
      <>
        <div style={{ position: "relative", flexShrink: 0, padding: 4 }}>
          <button
            type="button"
            aria-label="Thông báo"
            title="Thông báo"
            onClick={() => setOpen(true)}
            style={{
              all: "unset",
              width: 32,
              height: 32,
              borderRadius: 8,
              background: open
                ? isMobileTopbar
                  ? "rgba(0,0,0,0.05)"
                  : "rgba(255, 255, 255, 0.15)"
                : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: isMobileTopbar ? "#295e18" : "#FFFFFF",
              fontSize: 18,
              transition: "all 0.25s",
              flexShrink: 0,
            }}
          >
            <BellOutlined
              style={{
                animation: open ? "none" : "bell-swing 4s infinite ease-in-out",
              }}
            />
          </button>
          {unread > 0 && (
            <span
              style={{
                position: "absolute",
                top: 3,
                right: 3,
                minWidth: 15,
                height: 15,
                padding: "1px 3px",
                borderRadius: 8,
                background: "#EF4444",
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                lineHeight: "15px",
                textAlign: "center",
                pointerEvents: "none",
                animation: "notif-pulse-red 2s infinite",
                boxSizing: "border-box",
              }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
        <Drawer
          placement="bottom"
          onClose={() => setOpen(false)}
          open={open}
          height="82vh"
          styles={{
            body: {
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
            header: { display: "none" },
          }}
          style={{
            borderRadius: "20px 20px 0 0",
            overflow: "hidden",
          }}
        >
          <PanelContent
            role={role}
            onNavigate={(path) => {
              setOpen(false);
              navigate(path);
              if (onCloseSidebar) onCloseSidebar();
            }}
            onClose={() => {
              setOpen(false);
              if (onCloseSidebar) onCloseSidebar();
            }}
            isMobile={true}
            isOpen={open}
            onShowBroadcastModal={(notification) => {
              setSelectedBroadcast(notification);
              setOpen(false);
              if (onCloseSidebar) onCloseSidebar();
            }}
          />
        </Drawer>
        <BroadcastDetailModal
          open={!!selectedBroadcast}
          onClose={handleCloseBroadcastModal}
          broadcast={broadcastDetail}
          loading={loadingDetail}
        />
      </>
    );
  }

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="topRight"
        arrow={false}
        overlayInnerStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
        overlayStyle={{
          boxShadow: "0 10px 32px rgba(15, 23, 42, 0.18)",
          zIndex: 1050,
        }}
        content={
          <PanelContent
            role={role}
            onNavigate={(path) => {
              navigate(path);
              setOpen(false);
              if (onCloseSidebar) onCloseSidebar();
            }}
            onClose={() => {
              setOpen(false);
              if (onCloseSidebar) onCloseSidebar();
            }}
            isMobile={false}
            isOpen={open}
            onShowBroadcastModal={(notification) => {
              setSelectedBroadcast(notification);
              setOpen(false);
              if (onCloseSidebar) onCloseSidebar();
            }}
          />
        }
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            aria-label="Thông báo"
            title="Thông báo"
            style={{
              all: "unset",
              width: 32,
              height: 32,
              borderRadius: 8,
              background: open ? "rgba(255, 255, 255, 0.15)" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#FFFFFF",
              fontSize: 18,
              transition: "all 0.25s",
            }}
            onMouseEnter={(e) => {
              if (!open)
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            }}
            onMouseLeave={(e) => {
              if (!open) e.currentTarget.style.background = "transparent";
            }}
          >
            <BellOutlined
              style={{
                animation: open ? "none" : "bell-swing 4s infinite ease-in-out",
              }}
            />
          </button>
          {unread > 0 && (
            <span
              style={{
                position: "absolute",
                top: -3,
                right: -5,
                minWidth: 15,
                height: 15,
                padding: "1px 3px",
                borderRadius: 8,
                background: "#EF4444",
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                lineHeight: "15px",
                textAlign: "center",
                pointerEvents: "none",
                animation: "notif-pulse-red 2s infinite",
                boxSizing: "border-box",
              }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
      </Popover>
      <BroadcastDetailModal
        open={!!selectedBroadcast}
        onClose={handleCloseBroadcastModal}
        broadcast={broadcastDetail}
        loading={loadingDetail}
      />
    </>
  );
}

// ─── Constants for Broadcast Detail modal ───
const PRIORITY_COLOR = {
  urgent: "red",
  important: "orange",
  normal: "default",
};

const PRIORITY_ICON_LABEL = {
  urgent: "🚨 Khẩn cấp",
  important: "⚠️ Quan trọng",
  normal: "📌 Bình thường",
};

function BroadcastDetailModal({ open, onClose, broadcast, loading }) {
  const normPriority = broadcast?.priority
    ? broadcast.priority === "critical"
      ? "urgent"
      : broadcast.priority === "high"
        ? "important"
        : broadcast.priority === "medium"
          ? "normal"
          : broadcast.priority.toLowerCase()
    : "normal";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      width={540}
      title={null}
      destroyOnClose
      centered
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 0",
          }}
        >
          <Spin tip="Đang tải chi tiết thông báo..." />
        </div>
      ) : (
        broadcast && (
          <>
            <div
              style={{
                paddingBottom: 16,
                marginBottom: 16,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "#14532d",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <BellOutlined style={{ color: "#fff", fontSize: 14 }} />
                </div>
                <div>
                  <Text
                    style={{
                      fontSize: 13,
                      color: "#9CA3AF",
                      display: "block",
                      fontWeight: 600,
                      marginBottom: -5,
                    }}
                  >
                    {broadcast.author_id?.fullName || "Quản trị viên"}
                  </Text>
                  <Text style={{ fontSize: 10, color: "#9CA3AF" }}>
                    {dayjs(broadcast.createdAt).format("DD/MM/YYYY HH:mm")}
                  </Text>
                </div>
              </div>
              <Title level={5} style={{ margin: "10px 0 6px" }}>
                {broadcast.title || broadcast.name}
              </Title>
              <Space size={6} wrap>
                <Tag style={{ fontSize: 11, borderRadius: 6 }}>
                  {broadcast.category?.name ||
                    broadcast.category ||
                    "Thông báo"}
                </Tag>
                <Tag
                  color={PRIORITY_COLOR[normPriority]}
                  style={{ fontSize: 11, borderRadius: 6 }}
                >
                  {PRIORITY_ICON_LABEL[normPriority]}
                </Tag>
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              {broadcast.bannerPreview && (
                <img
                  src={getFullUrl(broadcast.bannerPreview)}
                  alt="banner"
                  style={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginBottom: 12,
                  }}
                />
              )}
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.75,
                  color: "#374151",
                }}
                dangerouslySetInnerHTML={{ __html: broadcast.content }}
              />
            </div>
          </>
        )
      )}
    </Modal>
  );
}
